/**
 * Horeca Food Store (Pinia)
 *
 * @version 1.2.0
 * @description Gestisce i prodotti HORECA-FOOD (company MKT, settore 6),
 *              provenienti dal middleware brand (non da Metodo). La ricerca
 *              è cross-brand: un'unica query interroga tutti i brand attivi.
 *              I prodotti arrivano già "appiattiti" dal HorecaProductResource
 *              (customer_price, supplier_price, UM, brand_code/brand_name,
 *              images, horeca:true) — niente price_lists/stocks annidati.
 *
 *              v1.1.0 (Fase 6): getProduct cross-brand via
 *              /horeca-food/products/{code} (il dettaglio non conosce il brand
 *              a priori); fetchDatasheet (blob via axios, JWT-safe);
 *              uploadImage/deleteImage per l'override immagine manuale.
 *
 *              v1.2.0 (Gestione autonoma, Fase 2): fetchCanManage + flag
 *              canManage (gating MKT6 verificato lato BE) per mostrare/nascondere
 *              le viste di gestione marchi/categorie/prodotti custom.
 */

import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { standardizeErrorMessage } from '@/utils/utils'
import { useNavigationStore } from '@/stores/useNavigationStore'
import type { Pagination, ApiRequestOptions } from '@/stores/types'

// =============================================================================
// Store
// =============================================================================

export const useHorecaFoodStore = defineStore('horecaFood', () => {
  const { companyApiUrl } = storeToRefs(useNavigationStore())

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const loading = ref(false)
  const products = ref<any[]>([])
  const productsPagination = ref<Pagination | null>(null)
  const currentOptions = ref<ApiRequestOptions>({})
  const categories = ref<any[]>([])

  /**
   * Permesso di GESTIONE (creare/modificare marchi, categorie, prodotti custom).
   * `null` = non ancora verificato; `true`/`false` = esito dal BE.
   * La verita' del gating resta lato BE (endpoint /horeca-food/can-manage):
   * qui teniamo solo la cache del flag per mostrare/nascondere UI e route.
   */
  const canManage = ref<boolean | null>(null)

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Verifica col BE se l'utente corrente puo' gestire i contenuti HORECA-FOOD
   * custom (admin/super-admin oppure agente abilitato MKT 6). Memorizza il
   * flag in `canManage`. Idempotente: il chiamante puo' invocarla a ogni
   * mount, l'esito sovrascrive la cache.
   */
  async function fetchCanManage(): Promise<boolean> {
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/horeca-food/can-manage`)
      canManage.value = data?.can === true
      return canManage.value
    } catch (error) {
      // In caso di errore (rete/permessi) si nega: nessuna UI di gestione.
      canManage.value = false
      return false
    }
  }

  /**
   * Lista prodotti cross-brand. Stessa superficie di fetchProducts (Metodo):
   * accetta itemsPerPage/all/searchBy/sortBy. Ritorna { data, pagination }.
   */
  async function fetchProducts(options?: ApiRequestOptions): Promise<any> {
    loading.value = true
    if (options != null) currentOptions.value = options

    try {
      const { data } = await axios.get(`${companyApiUrl.value}/horeca-food/products`, {
        params: options ?? currentOptions.value,
      })
      products.value = data.data ?? []
      productsPagination.value = data.pagination ?? null
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Albero categorie di tutti i brand attivi. Ogni nodo porta brand_code.
   */
  async function fetchCategories(): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.get(`${companyApiUrl.value}/horeca-food/categories`)
      categories.value = data.data ?? []
      return data.data ?? []
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Dettaglio singolo prodotto per codice, CROSS-BRAND. La detail page riceve
   * solo il codice (non il brand): l'endpoint cerca su tutti i brand attivi.
   */
  async function getProduct(productCode: string): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.get(
        `${companyApiUrl.value}/horeca-food/products/${encodeURIComponent(productCode)}`
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Crea un PRODOTTO CUSTOM sotto un brand (Fase 8A). Il codice e' LIBERO e
   * scelto dall'utente (obbligatorio, univoco per brand: il BE risponde 422 in
   * caso di collisione, col messaggio da mostrare com'e'). Nasce published +
   * custom, con un record prezzo vuoto da compilare nel dettaglio. La categoria
   * e' opzionale e deve appartenere allo stesso brand.
   *
   * Endpoint per-brand (brand nel PATH, non nel body):
   *   POST /{company}/horeca/{brand}/products
   *
   * Ritorna il prodotto creato (shape HorecaProductResource, 201).
   */
  async function createProduct(
    brand: string,
    payload: { code: string; description: string; um?: string | null; category_id?: number | null },
  ): Promise<any> {
    loading.value = true
    try {
      const body: Record<string, unknown> = {
        code: payload.code.trim(),
        description: payload.description.trim(),
      }
      if (payload.um != null && payload.um.trim() !== '') body.um = payload.um.trim()
      if (payload.category_id != null) body.category_id = payload.category_id

      const { data } = await axios.post(
        `${companyApiUrl.value}/horeca/${encodeURIComponent(brand)}/products`,
        body,
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Aggiorna un PRODOTTO CUSTOM (Fase 8B). Mappa
   *   PUT /{company}/horeca/{brand}/products/{code}
   * Campi modificabili: description, um, category_id, supplier_price,
   * customer_price. SOLO sui custom (il BE risponde 403 sugli ufficiali); il
   * codice e' immutabile e non viene mai inviato.
   *
   * IMPORTANTE — semantica assente vs null: il BE decide cosa toccare con
   * `$request->has(...)`, e per `um`/`category_id`/`customer_price` ammette
   * esplicitamente `null` (azzeramento). Inoltriamo quindi una chiave SOLO se
   * presente nel payload (`'key' in payload`), preservando il valore `null`
   * com'e'. Omettere una chiave = lasciare invariato quel campo lato BE.
   *
   * Ritorna il prodotto aggiornato (shape HorecaProductResource).
   */
  async function updateProduct(
    brand: string,
    productCode: string,
    payload: {
      description?: string
      long_description?: string | null
      um?: string | null
      category_id?: number | null
      supplier_price?: number | null
      customer_price?: number | null
      custom_attributes?: Array<{ key: string; value: string }> | null
    },
  ): Promise<any> {
    loading.value = true
    try {
      const body: Record<string, unknown> = {}
      if ('description' in payload && payload.description != null) {
        body.description = payload.description.trim()
      }
      // I seguenti accettano null (azzeramento): distinguo "assente" da "null".
      if ('long_description' in payload) {
        body.long_description =
          payload.long_description != null && payload.long_description.trim() !== ''
            ? payload.long_description
            : null
      }
      if ('um' in payload) body.um = payload.um != null ? payload.um.trim() : null
      if ('category_id' in payload) body.category_id = payload.category_id ?? null
      if ('supplier_price' in payload) body.supplier_price = payload.supplier_price ?? null
      if ('customer_price' in payload) body.customer_price = payload.customer_price ?? null
      if ('custom_attributes' in payload) {
        // Tiene solo le righe con chiave non vuota (allineato al BE).
        const attrs = (payload.custom_attributes ?? [])
          .map((a) => ({ key: (a.key ?? '').trim(), value: a.value ?? '' }))
          .filter((a) => a.key !== '')
        body.custom_attributes = attrs
      }

      const { data } = await axios.put(
        `${companyApiUrl.value}/horeca/${encodeURIComponent(brand)}/products/${encodeURIComponent(productCode)}`,
        body,
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Scarica la scheda tecnica (PDF) di un prodotto food come blob, via axios
   * (l'interceptor allega il JWT — window.open non lo farebbe). Ritorna un
   * object URL che il chiamante può aprire/scaricare e poi revocare.
   * Su 404 l'errore propaga (il FE mostra "Scheda tecnica non disponibile").
   */
  async function fetchDatasheet(brand: string, productCode: string): Promise<string> {
    loading.value = true
    try {
      const response = await axios.get(
        `${companyApiUrl.value}/horeca/${encodeURIComponent(brand)}/products/${encodeURIComponent(productCode)}/datasheet`,
        { responseType: 'blob' }
      )
      const blob = new Blob([response.data], { type: 'application/pdf' })
      return URL.createObjectURL(blob)
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Carica un'immagine OVERRIDE (la nostra) per un prodotto food. Vince
   * sull'immagine ufficiale remota. Ritorna il prodotto aggiornato.
   */
  async function uploadImage(brand: string, productCode: string, blob: Blob, filename: string): Promise<any> {
    loading.value = true
    try {
      const formData = new FormData()
      formData.append('image', blob, filename)
      const { data } = await axios.post(
        `${companyApiUrl.value}/horeca/${encodeURIComponent(brand)}/products/${encodeURIComponent(productCode)}/image`,
        formData,
        { headers: { 'content-type': 'multipart/form-data' } }
      )
      return data
    } catch (error: any) {
      if (error?.data?.errors?.image && Array.isArray(error.data.errors.image)) {
        throw { message: error.data.errors.image[0] }
      }
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Rimuove l'override manuale: il prodotto torna all'immagine ufficiale
   * remota (se presente). Ritorna il prodotto aggiornato.
   */
  async function deleteImage(brand: string, productCode: string): Promise<any> {
    loading.value = true
    try {
      const { data } = await axios.delete(
        `${companyApiUrl.value}/horeca/${encodeURIComponent(brand)}/products/${encodeURIComponent(productCode)}/image`
      )
      return data
    } catch (error) {
      throw standardizeErrorMessage(error)
    } finally {
      loading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    loading,
    products,
    productsPagination,
    currentOptions,
    categories,
    canManage,
    fetchCanManage,
    fetchProducts,
    fetchCategories,
    getProduct,
    createProduct,
    updateProduct,
    fetchDatasheet,
    uploadImage,
    deleteImage,
  }
})
